import { Skeleton } from '@shigo-ui/ui';

export interface DashboardDetailsPageSkeletonProps {
  label: string;
  showBackAction?: boolean | undefined;
  showHeaderAction?: boolean | undefined;
  metadataItemCount?: number | undefined;
  showSecondaryAction?: boolean | undefined;
  tabCount?: number | undefined;
  contentRowCount?: number | undefined;
}

export function DashboardDetailsPageSkeleton({
  label,
  showBackAction = true,
  showHeaderAction = false,
  metadataItemCount = 0,
  showSecondaryAction = false,
  tabCount = 1,
  contentRowCount = 3,
}: DashboardDetailsPageSkeletonProps) {
  const metadataItems = skeletonIndexes(metadataItemCount);
  const tabs = skeletonIndexes(tabCount);
  const contentRows = skeletonIndexes(contentRowCount);

  return (
    <main className="min-w-0 overflow-x-clip">
      <div role="status" aria-label={label} data-slot="dashboard-details-page-skeleton">
        <div className="border-border bg-card/60 border-b">
          <section className="page-gutter mx-auto flex w-full max-w-[1920px] flex-col gap-4 pt-5">
            {/* Same grid as DashboardDetailsHeader, so nothing moves when the data lands. */}
            <header className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-1">
              <div className="flex h-9 items-center">
                {showBackAction ? (
                  <Skeleton
                    className="h-9 w-9 shrink-0 rounded-md"
                    data-slot="dashboard-details-back-skeleton"
                  />
                ) : null}
              </div>
              <div className="flex min-h-9 min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
                <Skeleton
                  className="h-7 w-64 max-w-full"
                  data-slot="dashboard-details-title-skeleton"
                />
                {showHeaderAction || showSecondaryAction ? (
                  <div className="flex items-center">
                    {showHeaderAction ? (
                      <Skeleton
                        className="h-8 w-16 rounded-l-md rounded-r-none"
                        data-slot="dashboard-details-header-action-skeleton"
                      />
                    ) : null}
                    {showSecondaryAction ? (
                      <Skeleton
                        className="h-8 w-8 rounded-l-none rounded-r-md"
                        data-slot="dashboard-details-secondary-action-skeleton"
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
              {metadataItems.length > 0 ? (
                <div className="col-start-2 flex min-w-0 flex-wrap items-center gap-x-5 gap-y-1 py-0.5">
                  {metadataItems.map((index) => (
                    <Skeleton
                      key={index}
                      className="h-4 w-28 max-w-full"
                      data-slot="dashboard-details-metadata-item-skeleton"
                    />
                  ))}
                </div>
              ) : null}
            </header>

            <div
              className="flex h-10 items-end gap-6 overflow-hidden px-3"
              aria-hidden="true"
              data-slot="dashboard-details-tabs-skeleton"
            >
              {tabs.map((index) => (
                <Skeleton
                  key={index}
                  className="h-7 w-28 rounded-none"
                  data-slot="dashboard-details-tab-skeleton"
                />
              ))}
            </div>
          </section>
        </div>

        <section className="page-gutter mx-auto w-full max-w-[1920px] min-w-0 py-6">
          <div className="border-border bg-card overflow-hidden rounded-md border">
            <div className="border-border border-b p-4">
              <Skeleton className="h-5 w-40" />
            </div>
            <div className="grid gap-4 p-4" data-slot="dashboard-details-content-skeleton">
              {contentRows.map((index) => (
                <Skeleton
                  key={index}
                  className="h-12 w-full"
                  data-slot="dashboard-details-content-row-skeleton"
                />
              ))}
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

function skeletonIndexes(count: number) {
  return Array.from({ length: Math.max(0, Math.floor(count)) }, (_, index) => index);
}
